import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { LoadingSpinner } from '../../../shared/ui';
import {
  AdminBlogShell,
  AdminPageHeader,
  appendYowanContentPreviewQuery,
  usePreservedDomainSearch,
} from './AdminBlogListPage';
import {
  fetchYowanAdminPageContentsList,
  formatYowanAdminDateTime,
} from '../app/yowanContentClient';
const { yowanPageContent } = require('./pageContent');

const thumbnailColumnStyle = {
  width: '8.5rem',
  minWidth: '8.5rem',
};

const pageColumnStyle = {
  width: '9rem',
  minWidth: '9rem',
  whiteSpace: 'nowrap',
};

const sectionColumnStyle = {
  width: '11rem',
  minWidth: '11rem',
};

const descriptionColumnStyle = {
  minWidth: '24rem',
};

const statusColumnStyle = {
  width: '8rem',
  minWidth: '8rem',
  whiteSpace: 'nowrap',
};

const updatedAtColumnStyle = {
  width: '11.5rem',
  minWidth: '11.5rem',
  whiteSpace: 'nowrap',
};

const actionsColumnStyle = {
  width: '6rem',
  minWidth: '6rem',
  whiteSpace: 'nowrap',
};

const tableStyle = {
  minWidth: '78rem',
};

const imageStyle = {
  display: 'block',
  width: '7rem',
  aspectRatio: '16 / 10',
  objectFit: 'contain',
  borderRadius: '6px',
  background: 'rgba(47, 54, 61, 0.06)',
};

const emptyImageStyle = {
  ...imageStyle,
  display: 'grid',
  placeItems: 'center',
  color: 'rgba(47, 54, 61, 0.58)',
  fontSize: '0.75rem',
};

const getSlotImageUrl = (slot) =>
  slot.primary_image?.preview_url || slot.primary_image?.thumbnail_url || slot.primary_image?.url || '';

const AdminPageContentsListPage = () => {
  const content = yowanPageContent['/admin/content/page-contents'];
  const preservedDomainSearch = usePreservedDomainSearch();
  const location = useLocation();
  const savedPageContent = location.state?.savedPageContent === true;
  const refreshPageContentsAt = location.state?.refreshPageContentsAt || '';
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [notice, setNotice] = useState('');

  const loadRows = useCallback((isMountedRef) => {
    setIsLoading(true);
    setLoadError('');

    return fetchYowanAdminPageContentsList()
      .then((payload) => {
        if (isMountedRef.current) {
          setRows(payload.results);
        }
      })
      .catch(() => {
        if (isMountedRef.current) {
          setRows([]);
          setLoadError('一覧を読み込めませんでした。');
        }
      })
      .finally(() => {
        if (isMountedRef.current) {
          setIsLoading(false);
        }
      });
  }, []);

  useEffect(() => {
    const isMountedRef = { current: true };

    setNotice(savedPageContent ? 'ページコンテンツを保存しました。' : '');
    loadRows(isMountedRef);

    return () => {
      isMountedRef.current = false;
    };
  }, [loadRows, location.key, refreshPageContentsAt, savedPageContent]);

  if (!content) {
    return null;
  }

  return (
    <AdminBlogShell activeNavHref="/admin/content/page-contents" routeKey="/admin/content/page-contents">
      <AdminPageHeader
        title={content.header.title}
        paragraphs={content.header.paragraphs}
      />

      {notice ? <p className="yowan-admin-form__message">{notice}</p> : null}

      <div className="yowan-admin-resource-table-wrap c-surface-content">
        <table className="yowan-admin-resource-table" style={tableStyle}>
          <thead>
            <tr>
              <th scope="col" style={thumbnailColumnStyle}>画像</th>
              <th scope="col" style={pageColumnStyle}>ページ</th>
              <th scope="col" style={sectionColumnStyle}>表示枠</th>
              <th scope="col" style={descriptionColumnStyle}>説明</th>
              <th scope="col" style={statusColumnStyle}>公開</th>
              <th scope="col" style={updatedAtColumnStyle}>更新日</th>
              <th scope="col" style={actionsColumnStyle}>操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const imageUrl = getSlotImageUrl(row);

              return (
                <tr key={`${row.pageKey}-${row.slotKey}`}>
                  <td style={thumbnailColumnStyle}>
                    {imageUrl ? (
                      <img
                        src={imageUrl}
                        alt={row.primary_image?.alt_text || row.adminLabel}
                        style={imageStyle}
                      />
                    ) : (
                      <span style={emptyImageStyle}>画像なし</span>
                    )}
                  </td>
                  <td style={pageColumnStyle}>{row.pageLabel}</td>
                  <td style={sectionColumnStyle}>
                    <p className="yowan-admin-resource-table__title">{row.sectionLabel}</p>
                    <p className="yowan-admin-resource-table__subline">{row.adminLabel}</p>
                  </td>
                  <td style={descriptionColumnStyle}>{row.adminDescription || '-'}</td>
                  <td style={statusColumnStyle}>
                    <span
                      className={`yowan-admin-resource-table__status yowan-admin-resource-table__status--${
                        row.isPublished ? 'published' : 'draft'
                      }`}
                    >
                      {row.isPublished ? '公開中' : '非公開'}
                    </span>
                  </td>
                  <td style={updatedAtColumnStyle}>{formatYowanAdminDateTime(row.updatedAt)}</td>
                  <td style={actionsColumnStyle}>
                    <div className="yowan-admin-resource-table__actions">
                      <Link to={appendYowanContentPreviewQuery(row.editHref, preservedDomainSearch)}>編集</Link>
                    </div>
                  </td>
                </tr>
              );
            })}
            {!rows.length ? (
              <tr>
                <td colSpan="7">
                  {isLoading ? <LoadingSpinner size="sm" /> : loadError || '表示できるページコンテンツはありません。'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
    </AdminBlogShell>
  );
};

export default AdminPageContentsListPage;
